// src/padplanner/imageryStripRows.js
//
// SitePlanner's adapter for ImageryStrip (footprint restyle S-e slice 3): the
// epochs answer (GET /padplanner/…/epochs: {collects, reference}) as the
// strip's rows, for the plan page, the editor, New plan and the plans list.
//
//   captures   "Imagery": each collect's date on its chip and the capture in
//              its tooltip (imageryLabel); the pin (or New plan's base)
//              marked; a synthetic_test capture badged (DI4); the page's
//              actions (Pin, Unpin) at the row's end; an empty row says why.
//   reference  "Sentinel-2 · context only", quiet, beneath, only when there is
//              a composite; standing alone under an empty captures row (DB's
//              answer 6). A chip reads "Spring 2025" and its tooltip names the
//              composite's window (DB's answer 2). Never marked: Sentinel-2
//              is not pinned.
//
// Coverage at the map center (S-e5, DB 2026-11-03), on both rows, from the
// shared logic (layers/imagery/entitledCoverage.js) over the map's own
// GET /imagery/entitled answer, recomputed as the map pans: covered at full
// strength; not covered dimmed, "no coverage at map center"; covered but
// zoomed out past the capture's minzoom at full strength with the zoom-in
// mark, "Zoom in to see this capture (from z<minzoom>)". Before the answer
// nothing is dimmed or marked.
//
// Pure: no state, no fetch, no map.

import { captureProduct, chipDate, imageryLabel, isSyntheticCapture } from "./planVocabulary";
import { formatReferenceDate, referenceWindow } from "../layers/imagery/formatImageryDate";
import { NO_COVERAGE_TEXT, coverageAt, zoomInText, zoomInTo } from "../layers/imagery/entitledCoverage";

export const CAPTURES_ROW_LABEL = "Imagery";
export const REFERENCE_ROW_LABEL = "Sentinel-2 · context only";

const underscored = (d) => String(d ?? "").replace(/-/g, "_");

/** A chip's coverage, zoom-in and tooltip at the map center; nothing is added with no answer to read. */
function atCenter(e, title, view) {
  if (!view) return { title };
  const coverage = coverageAt(view.areas, e, view.center);
  const zoomIn = zoomInTo(e, coverage, view.zoom);
  const why = coverage === "none" ? NO_COVERAGE_TEXT : zoomIn !== null ? zoomInText(zoomIn) : null;
  return { title: why ? `${title} — ${why}` : title, coverage, zoomIn };
}

function referenceChip(e, cursorKey, view) {
  const range = referenceWindow(e.acquisition_date);
  return {
    key: e.dataset_key,
    label: formatReferenceDate(underscored(e.acquisition_date)) || chipDate(e),
    selected: e.dataset_key === cursorKey,
    mark: null,
    synthetic: false,
    ...atCenter(e, `${range ? `${captureProduct(e)} · ${range}` : imageryLabel(e)} · context only`, view),
  };
}

/**
 * The strip's chip click on every SitePlanner page (S-e5): select the chip's
 * capture; a zoom-in chip also zooms the map to the capture's minzoom, keeping
 * the center (PadPlannerMap's zoomTo). `select` gets the chip too: a page whose
 * click toggles (the new plan form's base) selects, never un-selects, on a
 * zoom-in chip, which is a click to see the capture.
 * @param {(key: string, chip: object) => void} select
 * @param {{zoomTo: (z: number) => void}} strip  PadPlannerMap's bottom() argument
 */
export function selectAndZoom(select, strip) {
  return (_row, key, chip) => {
    select(key, chip);
    if (Number.isInteger(chip?.zoomIn)) strip.zoomTo(chip.zoomIn);
  };
}

/**
 * @param {object} p
 * @param {{collects?: object[], reference?: object[]}|null} p.lanes
 * @param {string|null} [p.cursorKey]  the capture shown
 * @param {string|null} [p.markKey]    the pinned (or chosen base) capture
 * @param {string} [p.markLabel]
 * @param {string} [p.emptyText]       said when there are no collects
 * @param {import("react").ReactNode} [p.end]  the captures row's actions
 * @param {Map<string, object|null>|null} [p.areas]  entitledAreas() of the map's answer (S-e5); null: not in yet
 * @param {[number, number]|null} [p.center]  the map center
 * @param {number|null} [p.zoom]
 * @returns {object[]|null}
 */
export function sitePlannerRows({ lanes, cursorKey = null, markKey = null, markLabel = "pinned", emptyText, end, areas, center = null, zoom = null }) {
  if (!lanes) return null;
  // undefined areas: a caller that does not draw coverage (no fields added).
  const view = areas === undefined ? null : { areas, center, zoom };
  const collects = Array.isArray(lanes.collects) ? lanes.collects : [];
  const reference = Array.isArray(lanes.reference) ? lanes.reference : [];
  const rows = [{
    id: "captures", label: CAPTURES_ROW_LABEL, tone: "primary", emptyText, end,
    chips: collects.map((e) => ({
      key: e.dataset_key,
      label: chipDate(e),
      selected: e.dataset_key === cursorKey,
      mark: markKey && e.dataset_key === markKey ? markLabel : null,
      synthetic: isSyntheticCapture(e),
      ...atCenter(e, imageryLabel(e), view),
    })),
  }];
  if (reference.length) {
    rows.push({ id: "reference", label: REFERENCE_ROW_LABEL, tone: "quiet", chips: reference.map((e) => referenceChip(e, cursorKey, view)) });
  }
  return rows;
}
